/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { observer } from 'mobx-react-lite'
import {
  FloatingPortal,
  autoUpdate,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  arrow,
  FloatingArrow,
} from '@floating-ui/react'
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid'
import { BellIcon as NavBellIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import { Bell as BellIcon, CheckCircle, Settings, SparksSolid } from 'iconoir-react'
import { useState, useEffect, useRef } from 'react'
import Button from '../Button'
import { Dropdown } from '../shared/styled/Dropdown'
import { Toggle } from '../Toggle'
import LazyNotificationCard from './LazyNotificationCard'
import { CountBadge } from 'app/components/inbox/CountBadge'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useSettings } from 'app/hooks/useSettings'
import { playChime } from 'app/lib/alertChime'
import { useUser, useWorkspace } from 'app/utils'
import { useAuthStore, useNotificationStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'

// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
const NotificationPanel = observer(function NotificationPanel() {
  const me = useUser()
  const notificationStore = useNotificationStore()
  const [isOpen, setIsOpen] = useState(false)
  const pollingRef = useRef<NodeJS.Timer>(undefined)
  const [settings, setSettings] = useSettings<boolean>(`notificationsShowUnread`)
  const [showOnlyUnread, setShowOnlyUnread] = useState(false)
  const arrowRef = useRef(null)
  const audioRef = useRef<HTMLAudioElement | undefined>(undefined)
  const workspace = useWorkspace()
  const { products } = useAuthStore()
  const disabled = !products?.includes(`AI Agent`) && !products?.includes(`CRM`)
  const aiAgentOnlyNotifications = useFeatureToggle(`ai-agent-only-notifications`)
  const isAiAgentOnly = products?.includes(`AI Agent`) && !products?.includes(`CRM`)
  const hidden = disabled || (isAiAgentOnly && !aiAgentOnlyNotifications)

  // Single source (ADR-0002): render the shared interned instances the store holds.
  const notifications = notificationStore.hits

  let floatingData = useFloating({
    placement: `right-end`,
    middleware: [
      offset({ mainAxis: 24, crossAxis: 74 }),
      arrow({
        element: arrowRef,
      }),
    ],
    open: isOpen,
    onOpenChange: (shouldOpen) => {
      if (!disabled) {
        setIsOpen(shouldOpen)
      }
    },
    whileElementsMounted: autoUpdate,
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingData.context),
    useDismiss(floatingData.context, { outsidePress: true }),
  ])

  const handleMarkAllAsRead = async () => {
    await notificationStore.markAllRead()
    await notificationStore.resetUnseen()
    document.title = document.title.replace(/\(\d+\) /, ``)
  }

  const handleMarkAsRead = async (read: boolean, notificationId: string) => {
    // markRead merges `read` into the shared instance and moves the unread count
    // optimistically, reconciling on the mutation result (ADR-0002).
    bg(async () => notificationStore.markRead(notificationId, read))
    bg(async () => notificationStore.resetUnseen())
    document.title = document.title.replace(/\(\d+\) /, ``)
  }

  const handleShowOnlyUnread = async (value: boolean) => {
    await setSettings(`notificationsShowUnread`, value)
    setShowOnlyUnread(value)
  }

  const handleForceClosePanel = () => {
    setIsOpen(false)
  }

  const handleNavigateToSettings = () => {
    window.location.href = `/settings/users/${me!.id}/notifications`
  }

  const scrollHandler: React.UIEventHandler<HTMLDivElement> = (event: any) => {
    let element = event.target
    if (
      notificationStore.hasMore &&
      !notificationStore.listLoading &&
      Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) < 800
    ) {
      bg(async () => notificationStore.fetchNextPage())
    }
  }

  // Seed the shared unread count and the tab title once the panel is active.
  useEffect(() => {
    if (hidden) return
    bg(async () => {
      await notificationStore.fetchUnreadCount()
      const count = notificationStore.unreadBadgeCount
      if (count > 0) document.title = `(${count}) ${document.title.replace(/\(\d+\) /, ``)}`
    })
  }, [hidden])

  useEffect(() => {
    if (!hidden) return
    document.title = document.title.replace(/\(\d+\) /, ``)
    setIsOpen(false)
  }, [hidden])

  useEffect(() => {
    if (hidden || !isOpen) return
    bg(async () => notificationStore.resetUnseen())
    document.title = document.title.replace(/\(\d+\) /, ``)
  }, [hidden, isOpen])

  // Load the first page of the shared list on activation. The store interns, dedupes and
  // paginates; infinite scroll calls fetchNextPage, so the panel never holds its own copy.
  useEffect(() => {
    if (hidden) return
    bg(async () =>
      notificationStore.fetchList({
        pagination: { limit: 20 },
        sort: { field: `triggerDate`, direction: `DESC` },
      }),
    )
  }, [hidden])

  // Poll for new arrivals: refetch page 0 of the shared list and reseed the unread count.
  // The store dedupes by id, so a refetch does not duplicate rows the panel already shows.
  useEffect(() => {
    if (hidden) return
    pollingRef.current = setInterval(() => {
      bg(async () => {
        const before = new Set(notificationStore.hits.map((n) => n.id))
        await notificationStore.fetchList({
          pagination: { limit: 20 },
          sort: { field: `triggerDate`, direction: `DESC` },
        })
        const arrivedIds = notificationStore.hits.map((n) => n.id).filter((id): id is string => !!id && !before.has(id))
        await notificationStore.fetchUnreadCount()
        const count = notificationStore.unreadBadgeCount
        if (count > 0) {
          document.title = `(${count}) ${document.title.replace(/\(\d+\) /, ``)}`
          if (arrivedIds.length) playChime(audioRef, arrivedIds)
        }
      })
    }, 30000)
    return () => clearInterval(pollingRef.current)
  }, [hidden])

  useEffect(() => {
    if (hidden) {
      // Drop the shared list so a later reactivation reloads page 0 cleanly. The unread count
      // resets with it; the seed effect refills both when the panel becomes visible again.
      notificationStore.reset()
      return
    }
  }, [hidden])

  useEffect(() => {
    if (typeof settings !== `undefined`) {
      setShowOnlyUnread(settings)
    }
  }, [settings])

  if (!me) return null

  if (hidden) return null

  return (
    <>
      <div
        className={classNames(
          disabled && `!cursor-not-allowed`,
          `mb-2 flex h-9 cursor-pointer items-center space-x-2 pl-1.5 pr-2 text-default hover:rounded-md hover:bg-(--fg-strong)/4 ${
            isOpen && `rounded-md bg-surface-muted`
          }`,
        )}
        ref={floatingData.refs.setReference}
        {...getReferenceProps()}
      >
        <div className="flex h-full w-9 items-center justify-center">
          {/* Same params as the top-level nav icons in lib/sidebarItems. */}
          <NavBellIcon
            size={20}
            weight="bold"
            className={classNames(disabled && `!text-minimal`, `text-default`)}
            aria-hidden="true"
          />
        </div>
        <span className={`flex h-9 grow items-center text-left text-heading-xs text-strong`}>Notifications</span>
        <CountBadge value={notificationStore.unreadBadgeCount} className="ml-auto" isActive={isOpen} />
      </div>
      {isOpen && (
        <FloatingPortal>
          <div
            className="z-50 grid h-[800px] w-[600px] grid-rows-[auto_1fr] rounded-2xl border bg-surface-raised shadow-elevation-8"
            ref={floatingData.refs.setFloating}
            {...getFloatingProps()}
            style={{ ...floatingData.floatingStyles }}
          >
            <FloatingArrow
              ref={arrowRef}
              width={28}
              height={14}
              style={{ fill: `var(--surface)` }}
              context={floatingData.context}
            />
            <div className="flex items-center justify-between border-b-2 border-default p-5">
              <h5>Notifications</h5>
              <div className="flex gap-2">
                <Toggle
                  label="Only show unread"
                  defaultValue={showOnlyUnread}
                  handleToggle={handleShowOnlyUnread}
                  showLabelOnLeft
                />
                <Dropdown
                  className="flex w-max flex-col !rounded-md py-2 shadow-elevation-5"
                  placement="bottom-end"
                  button={<EllipsisVerticalIcon className="h-6 w-6 cursor-pointer text-default" />}
                  closeOnClick
                >
                  <div
                    className="flex cursor-pointer items-center gap-3 px-4 py-2 text-body-sm text-default hover:bg-(--menu-hover)"
                    onClick={() => handleMarkAllAsRead()}
                  >
                    <CheckCircle className="h-5 w-5" />
                    <p>Mark all as read</p>
                  </div>
                  <div
                    className="flex cursor-pointer items-center gap-3 px-4 py-2 text-body-sm text-default hover:bg-(--menu-hover)"
                    onClick={() => handleNavigateToSettings()}
                  >
                    <Settings className="h-5 w-5" />
                    <p>Notification settings</p>
                  </div>
                </Dropdown>
              </div>
            </div>
            <div className="divide-border-subtle flex flex-col divide-y overflow-y-auto px-5" onScroll={scrollHandler}>
              {notificationStore.listLoaded && notifications.length === 0 && (
                <div className="flex h-full w-full flex-col items-center justify-center">
                  <BellIcon className="h-12 w-auto text-strong" strokeWidth={1.5} />
                  <p className="mt-4 text-heading-xl text-strong">No Notifications</p>
                  <p className="mt-4 px-16 text-center text-body-md text-subtle">
                    You will receive notifications for calls, forms, texts, and PMS related action items in this panel.
                  </p>
                  <Button
                    label="Manage Preferences"
                    mode="white"
                    size="sm"
                    className="mt-4"
                    onClick={() => handleNavigateToSettings()}
                  />
                </div>
              )}
              {showOnlyUnread &&
                notifications &&
                notifications.length > 0 &&
                notifications.filter((notification) => !notification.read).length === 0 && (
                  <div className="flex h-full w-full flex-col items-center justify-center">
                    <SparksSolid className="h-12 w-auto text-strong" strokeWidth={1.5} />
                    <p className="mt-4 text-heading-xl text-strong">All Caught Up</p>
                    <p className="mt-4 px-16 text-center text-body-md text-subtle">
                      You're up to date. We'll notify you when there's something new to review.
                    </p>
                  </div>
                )}
              {notifications
                ?.filter((notification) => (showOnlyUnread ? !notification.read : true))
                .map((notification) => (
                  <div key={notification.id} className="w-full py-4">
                    <LazyNotificationCard
                      notification={notification}
                      onForceClosePanel={handleForceClosePanel}
                      onMarkAsRead={handleMarkAsRead}
                    />
                  </div>
                ))}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  )
})

export default NotificationPanel
